import React, { useMemo, useRef, useEffect, useState, useCallback, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { useSearchParams, useLocation } from "react-router-dom";
import { FiSearch, FiUsers, FiInbox, FiGrid, FiLoader } from "react-icons/fi";
import "./CollaboratorsLayout.css";

// ============================================================================
// LAZY LOADED TAB COMPONENTS - Only loaded when first visited
// ============================================================================
const NetworkDashboard = lazy(() => import("./NetworkDashboard"));
const DiscoverTab = lazy(() => import("./DiscoverTab"));
const NetworkTab = lazy(() => import("./NetworkTab"));
const RequestsTab = lazy(() => import("./RequestsTab"));

// Valid tab IDs
const VALID_TABS = ["dashboard", "discover", "network", "requests"];

// Tab Loading Fallback Component
const TabLoadingFallback = () => (
  <div className="collab-tab-loading">
    <FiLoader className="collab-tab-loading-spinner" />
    <span>Loading...</span>
  </div>
);

const CollaboratorsLayout = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Check if we're on a profile page (layout still mounted but URL changed)
  const isOnProfilePage = location.pathname.includes("/collaborators/profile/");
  
  // Store the last active tab to preserve it when viewing profiles
  const lastActiveTabRef = useRef("dashboard");
  
  // ============================================================================
  // STATE-OF-THE-ART: Track visited tabs - only mount once visited, never unmount
  // This gives us: lazy loading + state preservation + scroll position retention
  // ============================================================================
  const [visitedTabs, setVisitedTabs] = useState(() => new Set(["dashboard"]));
  
  // Get tab from URL (only when not on profile page)
  const tabFromUrl = VALID_TABS.includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "dashboard";
  
  // Use stored tab when on profile page, otherwise use URL tab
  const activeTab = isOnProfilePage ? lastActiveTabRef.current : tabFromUrl;
  
  // Mark tab as visited when it becomes active (triggers lazy load)
  useEffect(() => {
    if (!isOnProfilePage && !visitedTabs.has(activeTab)) {
      setVisitedTabs(prev => new Set([...prev, activeTab]));
    }
  }, [activeTab, isOnProfilePage, visitedTabs]);
  
  // Update ref whenever we're NOT on a profile page
  useEffect(() => {
    if (!isOnProfilePage) {
      lastActiveTabRef.current = tabFromUrl;
    }
  }, [isOnProfilePage, tabFromUrl]);

  // Handle tab change - updates URL instead of state
  const handleTabChange = useCallback((tabId) => {
    setSearchParams({ tab: tabId }, { replace: true });
  }, [setSearchParams]);

  const tabs = [
    {
      id: "dashboard",
      label: "My Network",
      icon: FiGrid,
      color: "#8b5cf6",
    },
    {
      id: "discover",
      label: "Discover",
      icon: FiSearch,
      color: "#3b82f6",
    },
    {
      id: "network",
      label: "Connections",
      icon: FiUsers,
      color: "#10b981",
    },
    {
      id: "requests",
      label: "Requests",
      icon: FiInbox,
      color: "#f59e0b",
    },
  ];

  // ============================================================================
  // LAZY TAB COMPONENT MAPPING
  // Components are only instantiated when the tab is visited for the first time
  // ============================================================================
  const getTabComponent = useCallback((tabId) => {
    switch (tabId) {
      case "dashboard":
        return <NetworkDashboard />;
      case "discover":
        return <DiscoverTab />;
      case "network":
        return <NetworkTab />;
      case "requests":
        return <RequestsTab />;
      default:
        return null;
    }
  }, []);

  return (
    <div className="collab-layout">
      {/* Header */}
      <div className="collab-header">
        <h1 className="collab-header-title">Collaborators</h1>
        <p className="collab-header-subtitle">
          Find and connect with researchers worldwide to collaborate on projects
        </p>
      </div>

      {/* Navigation Tabs - Sticky */}
      <div className="collab-nav">
        <div className="collab-nav-inner">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <motion.button
                key={tab.id}
                className={`collab-nav-tab ${
                  activeTab === tab.id ? "active" : ""
                }`}
                onClick={() => handleTabChange(tab.id)}
                style={{ "--tab-accent": tab.color }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon className="collab-nav-icon" />
                <span className="collab-nav-label">{tab.label}</span>
                {activeTab === tab.id && (
                  <motion.div
                    className="collab-nav-indicator"
                    layoutId="activeTab"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* ====================================================================
          STATE-OF-THE-ART LAZY LOADING PATTERN
          ====================================================================
          - Tabs are only mounted when FIRST VISITED (lazy loading)
          - Once mounted, tabs NEVER unmount (state preservation)
          - Hidden tabs use visibility:hidden (scroll position retained)
          - Active tabs use visibility:visible
          - Suspense handles loading state for lazy components
          ==================================================================== */}
      <div className="collab-content-container">
        {VALID_TABS.map((tabId) => {
          // Only render tabs that have been visited at least once
          const hasBeenVisited = visitedTabs.has(tabId);
          const isActive = activeTab === tabId;
          
          if (!hasBeenVisited) {
            // Tab not yet visited - render nothing (no API calls!)
            return null;
          }
          
          return (
            <div
              key={tabId}
              className={`collab-content ${isActive ? "collab-content--active" : "collab-content--hidden"}`}
              aria-hidden={!isActive}
            >
              <Suspense fallback={<TabLoadingFallback />}>
                {getTabComponent(tabId)}
              </Suspense>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CollaboratorsLayout;
